/**
 * Parents area — /parents (behind the parental gate from the home).
 *
 * Language + volumes. Here will also go "remove ads" (M4) and the
 * pedagogical goals of each module (Teacher Approved).
 */

import { createSignal, For, onCleanup, Show } from "solid-js";
import { createRoute, Link, useNavigate } from "@tanstack/solid-router";
import {
  ArrowLeft,
  BadgeCheck,
  Check,
  Loader2,
  Mic,
  Music,
  ShieldOff,
  Volume2,
} from "@fleet/icons";
import type { DifficultyPreset } from "@melodimals/config/app-config";
import { LOCALE_DEFINITIONS, type SupportedLocale } from "@melodimals/config/locales";
import {
  isAndroid,
  isIapEnabled,
  isPremium,
  purchaseRemoveAds,
  restorePremium,
  subscribePremium,
} from "@melodimals/monetization";
import { rootRoute } from "@/routes/__root";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useI18n } from "@/providers/i18n-provider";
import { useAppConfig } from "@/providers/config-provider";
import { useSettings } from "@/providers/settings-provider";
import {
  gatePassSurvivesNavigation,
  hasGatePass,
  markGatePassed,
  resetGatePass,
} from "@/features/shell/gate-pass";
import { ParentalGate } from "@/features/shell/parental-gate";
import { cn } from "@/lib/cn";

/**
 * The gate also applies on direct access by URL: it is only bypassed with a
 * valid pass (set by the home or by this same route). On unmounting by
 * navigating away the pass is invalidated; if the unmount is the re-mount of the
 * tree when changing language (we are still in /parents) it is kept, so as not to
 * ask for the sum again in the middle of the change. Closing the gate without resolving
 * returns to the home.
 */
function GuardedParentsPage() {
  const navigate = useNavigate();
  const { gatePassMinutes } = useAppConfig().config().parental;
  const [unlocked, setUnlocked] = createSignal(hasGatePass());

  onCleanup(() => {
    // With a configured validity, the pass survives leaving /parents (the
    // adult does not resolve the sum again during that time); without it, it dies
    // here as always.
    if (globalThis.location.pathname !== "/parents" && !gatePassSurvivesNavigation()) {
      resetGatePass();
    }
  });

  return (
    <Show
      when={unlocked()}
      fallback={
        <ParentalGate
          open
          onSuccess={() => {
            markGatePassed(gatePassMinutes);
            setUnlocked(true);
          }}
          onDismiss={() => {
            void navigate({ to: "/" });
          }}
        />
      }
    >
      <ParentsPage />
    </Show>
  );
}

/** Difficulty presets, in the order they are offered to the adult. */
const DIFFICULTY_OPTIONS: { value: DifficultyPreset; label: MessageDescriptor }[] = [
  { value: "gentle", label: msg`Gentle` },
  { value: "normal", label: msg`Normal` },
  { value: "challenge", label: msg`Challenge` },
];

function ParentsPage() {
  const { locale, setLocale, isLoading, t } = useI18n();
  const settings = useSettings();
  const appConfig = useAppConfig();
  const [premium, setPremium] = createSignal(isPremium());
  const [busy, setBusy] = createSignal(false);
  onCleanup(subscribePremium(setPremium));

  const buyRemoveAds = () => {
    void (async () => {
      setBusy(true);
      await purchaseRemoveAds();
      setBusy(false);
    })();
  };

  const restore = () => {
    void (async () => {
      setBusy(true);
      await restorePremium();
      setBusy(false);
    })();
  };

  const handleSelect = (code: SupportedLocale) => {
    if (code === locale() || isLoading()) {
      return;
    }
    void setLocale(code);
  };

  return (
    <main class="kid-screen screen-enter mx-auto w-full max-w-2xl gap-8 p-6">
      <header class="flex items-center gap-3">
        <Link
          to="/"
          class="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card hover:bg-muted"
          aria-label={t(msg`Back`)}
        >
          <ArrowLeft class="h-5 w-5" />
        </Link>
        <h1 class="text-2xl font-bold tracking-tight">{t(msg`For parents`)}</h1>
        <Show when={isLoading()}>
          <Loader2 class="h-4 w-4 animate-spin text-muted-foreground" />
        </Show>
      </header>

      {/*
        Quitar anuncios — solo Android (en web/desktop no hay anuncios) y solo
        si el build declara la compra nativa (`VITE_IAP_ENABLED`, apagada por
        defecto). Esconder el botón es la parte MENOS importante del
        interruptor: aunque se pintara, `purchaseRemoveAds` no tiene producto
        que pedir y el plugin de Play Billing ni siquiera está registrado en el
        binario. Ver docs/auditoria/2026-08-13-interruptor-compra-nativa.md.
      */}
      <Show when={isAndroid() && isIapEnabled()}>
        <section class="space-y-3">
          <h2 class="text-lg font-semibold">{t(msg`Ads`)}</h2>
          <Show
            when={!premium()}
            fallback={
              <p class="flex items-center gap-2 rounded-xl border border-border bg-muted/30 p-4 text-sm">
                <BadgeCheck class="h-5 w-5 text-emerald-600" aria-hidden="true" />
                {t(msg`Ads removed. Thank you for supporting Melodimals!`)}
              </p>
            }
          >
            <div class="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={buyRemoveAds}
                disabled={busy()}
                class="flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60"
              >
                <ShieldOff class="h-4 w-4" aria-hidden="true" />
                {t(msg`Remove ads`)}
              </button>
              <button
                type="button"
                onClick={restore}
                disabled={busy()}
                class="rounded-xl border border-border px-5 py-3 text-sm disabled:opacity-60"
              >
                {t(msg`Restore purchases`)}
              </button>
            </div>
          </Show>
        </section>
      </Show>

      {/*
        Ajustes de juego — el nivel "adulto" de la configuración de la app
        (`app-config.ts`). Los tres mandos que un adulto querría mover sin
        esperar a una actualización: la dificultad de los mini-juegos, la voz
        del narrador y cuántos anuncios acepta como máximo. Todo se guarda en
        ESTE dispositivo y se aplica en caliente.
      */}
      <section class="space-y-4">
        <h2 class="text-lg font-semibold">{t(msg`Game settings`)}</h2>

        <div class="space-y-2">
          <p class="text-sm font-medium">{t(msg`Difficulty`)}</p>
          <p class="text-xs text-muted-foreground">
            {t(
              msg`Adjusts the length and pace of the mini-games. Applies next time a game starts.`,
            )}
          </p>
          <div class="grid grid-cols-3 gap-2" role="radiogroup" aria-label={t(msg`Difficulty`)}>
            <For each={DIFFICULTY_OPTIONS}>
              {(option) => (
                <button
                  type="button"
                  role="radio"
                  data-testid={`difficulty-${option.value}`}
                  aria-checked={appConfig.config().difficulty === option.value}
                  onClick={() => {
                    appConfig.update({ difficulty: option.value });
                  }}
                  class={cn(
                    "rounded-xl border p-3 text-sm font-semibold transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    appConfig.config().difficulty === option.value
                      ? "border-primary bg-primary/10"
                      : "border-border bg-card hover:border-primary/40",
                  )}
                >
                  {t(option.label)}
                </button>
              )}
            </For>
          </div>
        </div>

        <label class="flex items-center gap-4">
          <Mic class="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span class="flex-1 text-sm">{t(msg`Narrator voice`)}</span>
          <input
            type="checkbox"
            data-testid="narration-toggle"
            checked={appConfig.config().narration.enabled}
            onChange={(e) => {
              appConfig.update({ narrationEnabled: e.currentTarget.checked });
            }}
            class="h-6 w-6 accent-primary"
          />
        </label>

        {/*
          Tope de anuncios: el adulto solo puede pedir MENOS de los que ya
          permite el régimen infantil (§6). Por eso las opciones bajan de 2 a 0
          y no hay ninguna por encima — no es que se ignore pedir más: es que
          pedirlo no es una opción de la interfaz.
        */}
        <Show when={isAndroid() && !premium()}>
          <div class="space-y-2">
            <p class="text-sm font-medium">{t(msg`Ads per day at most`)}</p>
            <div class="grid grid-cols-3 gap-2" role="radiogroup">
              <For each={[2, 1, 0]}>
                {(value) => (
                  <button
                    type="button"
                    role="radio"
                    data-testid={`ads-per-day-${value}`}
                    aria-checked={appConfig.config().adCaps.interstitialPerDay === value}
                    onClick={() => {
                      appConfig.update({ interstitialPerDay: value });
                    }}
                    class={cn(
                      "rounded-xl border p-3 text-sm font-semibold transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      appConfig.config().adCaps.interstitialPerDay === value
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:border-primary/40",
                    )}
                  >
                    {value}
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>
      </section>

      <section class="space-y-4">
        <h2 class="text-lg font-semibold">{t(msg`Sound`)}</h2>
        <label class="flex items-center gap-4">
          <Music class="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span class="w-32 text-sm">{t(msg`Music volume`)}</span>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(settings.musicVolume() * 100)}
            onInput={(e) => {
              settings.setMusicVolume(Number(e.currentTarget.value) / 100);
            }}
            class="flex-1 accent-primary"
          />
        </label>
        <label class="flex items-center gap-4">
          <Volume2 class="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span class="w-32 text-sm">{t(msg`Sound volume`)}</span>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(settings.sfxVolume() * 100)}
            onInput={(e) => {
              settings.setSfxVolume(Number(e.currentTarget.value) / 100);
            }}
            class="flex-1 accent-primary"
          />
        </label>
      </section>

      <section class="space-y-3">
        <h2 class="text-lg font-semibold">{t(msg`Language`)}</h2>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup">
          <For each={LOCALE_DEFINITIONS}>
            {(def) => (
              <button
                type="button"
                role="radio"
                aria-checked={locale() === def.code}
                disabled={isLoading() && locale() !== def.code}
                onClick={() => {
                  handleSelect(def.code);
                }}
                class={cn(
                  "flex items-center gap-4 rounded-xl border p-4 text-left transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  "hover:border-primary/40 hover:bg-primary/5",
                  locale() === def.code ? "border-primary bg-primary/10" : "border-border bg-card",
                )}
              >
                <span class="text-2xl" aria-hidden="true">
                  {def.flag}
                </span>
                <span class="flex-1 min-w-0">
                  <span
                    class="block truncate text-sm font-semibold"
                    dir={def.isRTL ? "rtl" : "ltr"}
                  >
                    {def.label}
                  </span>
                  <span class="block truncate text-xs text-muted-foreground">
                    {def.englishLabel}
                  </span>
                </span>
                <Show when={locale() === def.code}>
                  <span class="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check class="h-3.5 w-3.5" />
                  </span>
                </Show>
              </button>
            )}
          </For>
        </div>
      </section>

      {/*
        Política de privacidad y términos. Google Play Families y la política de
        contenido infantil de Microsoft Store exigen que sean accesibles desde la
        app, no solo desde la ficha de la tienda; existían en `public/` pero no
        se enlazaban desde ninguna parte. La zona de padres es el sitio: está
        detrás de la puerta parental, que es justo el patrón que ambas tiendas
        piden para los enlaces que sacan al niño de la app.

        Son ficheros `.html` ESTÁTICOS, así que van con `<a>` y no con `<Link>`
        del router (que los trataría como rutas del SPA), y con `rel="noreferrer"`
        al abrirse fuera.
      */}
      <section class="space-y-3 border-t border-border pt-6 pb-2">
        <h2 class="text-lg font-semibold">{t(msg`Legal`)}</h2>
        <div class="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <a
            href="/privacy.html"
            target="_blank"
            rel="noreferrer"
            class="text-primary underline underline-offset-4"
          >
            {t(msg`Privacy policy`)}
          </a>
          <a
            href="/terms.html"
            target="_blank"
            rel="noreferrer"
            class="text-primary underline underline-offset-4"
          >
            {t(msg`Terms of use`)}
          </a>
        </div>
      </section>
    </main>
  );
}

export const parentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parents",
  component: GuardedParentsPage,
});
